import * as B from '@blueprintjs/core';
import React from 'react';
import {Link} from 'react-router-dom';

import {SidebarSection} from 'app/components/Sidebar';
import {formatCost} from 'app/utils/costUtils';

import {formatAcresLabel, sceneCountLabel} from '../format';
import type {DisplayGroup} from '../grouping';
import cs from './styles.module.scss';

export interface CartItemGroupCardProps {
  group: DisplayGroup<unknown>;
  /**
   * `receipt` is the confirmation's card: it stacks the group's portfolio under its name, and its
   * amount is what was charged rather than a running subtotal.
   */
  layout?: 'cart' | 'receipt';
  isCollapsed: boolean;
  onToggle: () => void;
  /** Links the group title. The title sits inside the collapse toggle, so clicks must not bubble. */
  titleUrl?: string;
  /** Rows in this group that need attention, surfaced in the header so a collapsed card still shows it. */
  needsAttentionCount?: number;
  children: React.ReactNode;
}

/** One section of a grouped cart or of the confirmation: a group's heading and totals over its rows. */
export const CartItemGroupCard: React.FC<CartItemGroupCardProps> = ({
  group,
  layout = 'cart',
  isCollapsed,
  onToggle,
  titleUrl,
  needsAttentionCount = 0,
  children,
}) => {
  const isReceipt = layout === 'receipt';
  const countAndAcres = `${sceneCountLabel(group.items.length)} · ${formatAcresLabel(group.acres)}`;

  const groupLabel = titleUrl ? (
    <Link to={titleUrl} title={`View ${group.label}`} onClick={(event) => event.stopPropagation()}>
      {group.label}
    </Link>
  ) : (
    group.label
  );

  // The cart folds the group's portfolio into its meta line; the receipt gives it its own line, so
  // the property reads as the heading and the portfolio as its context.
  const title = isReceipt ? (
    <span className={cs.receiptHeading}>
      <span className={cs.title}>{groupLabel}</span>
      {group.sublabel && <span className={cs.sublabel}>{group.sublabel}</span>}
    </span>
  ) : (
    <span className={cs.heading}>
      <span className={cs.title}>{groupLabel}</span>
      <span className={cs.meta}>{[countAndAcres, group.sublabel].filter(Boolean).join(' · ')}</span>
    </span>
  );

  return (
    <SidebarSection
      variant="card"
      bordered={false}
      title={title}
      rightElement={
        <span className={cs.headerTotals}>
          {needsAttentionCount > 0 && (
            <span className={cs.needsAttention}>
              <B.Icon icon="warning-sign" size={12} />
              <span>
                {`${needsAttentionCount} ${needsAttentionCount === 1 ? 'scene needs' : 'scenes need'} attention`}
              </span>
            </span>
          )}
          {isReceipt ? (
            <span className={cs.charged}>
              {[countAndAcres, formatCost(group.subtotalCents)].join(' · ')}
            </span>
          ) : (
            <span className={cs.subtotal}>
              <span className={cs.subtotalLabel}>Subtotal</span>
              <span className={cs.subtotalValue}>{formatCost(group.subtotalCents)}</span>
            </span>
          )}
        </span>
      }
      isOpen={!isCollapsed}
      onToggle={onToggle}
      bodyClassName={cs.body}
    >
      {children}
    </SidebarSection>
  );
};

export default CartItemGroupCard;
